// Single-series percent trend chart for the Workflow Hygiene Epic Linkage
// card (PRODOPSAI-383). Mirrors the visual idiom of the Throughput trend
// chart — Cartesian grid, labelled X/Y axes, smooth line — but stays a
// dedicated component because the existing TrendLineChart is hardwired to
// the Story/Bug/Task series shape. Adding a generic prop layer there would
// touch every consumer; this card has one series (linkagePercent) and one
// reference line, so a small dedicated component is the lower-blast-radius
// option.
//
// The dotted ReferenceLine sits at the success-tier `lowerBound` from the
// `epicLinkage` metric definition. Default seed places it at 90, but admin
// edits via /admin/metric-definitions move it without a code change. When
// the definition is unavailable (cold load or seed missing) the line is
// suppressed rather than rendering a stale literal.
//
// PRODOPSAI-383 reverses the original AC2.2.5 ("No 90% target line") from
// PRODOPSAI-377 — see ticket history for context.

import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { IEpicLinkageTrendPoint, IMetricDefinition } from '@skystone/shared';
import { useMetricDefinition } from '@/hooks/use-metric-definition';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';

const DEFAULT_HEIGHT = 120;

/**
 * Resolves the value for the dotted threshold reference line on the chart.
 * Epic Linkage is `higher_better`, so the success tier's `lowerBound`
 * encodes "hit this and you're in the green" — the natural target value.
 * Returns `null` when the definition is missing or has no success tier;
 * callers omit the reference line in that case.
 *
 * Exported for direct unit testing — recharts' SVG output in jsdom is
 * dimension-dependent and brittle to assert against. Pinning the lookup
 * here is the meaningful contract.
 */
export function successLowerBound(definition: IMetricDefinition | undefined): number | null {
  if (!definition) return null;
  const successTier = definition.tiers.find((t) => t.colorToken === 'success');
  return successTier?.lowerBound ?? null;
}

/**
 * Formats a Y-axis tick as a percent string (e.g. `25` → `"25%"`). Extracted
 * so coverage can pin the formatting contract — the inline arrow form
 * isn't reached in jsdom because recharts only invokes tick formatters
 * during measured axis layout.
 */
export function formatPercentTick(v: number): string {
  return `${v}%`;
}

/**
 * Formats a tooltip data point as `[value, name]` for recharts. `value`
 * may be `undefined` for points outside the data range; we coerce to `0`
 * rather than rendering `undefined%`. Same coverage rationale as the
 * tick formatter.
 */
export function formatLinkageTooltip(v: number | undefined): [string, string] {
  return [`${v ?? 0}%`, 'Linkage'];
}

interface EpicLinkageTrendChartProps {
  data: IEpicLinkageTrendPoint[];
  height?: number;
  /**
   * Optional. When supplied: x-axis ticks use the helper's `tickFormatter`,
   * the tooltip header gains a `"(so far)"` suffix on the current bucket,
   * and a dashed "Today" vertical reference line is drawn iff
   * `todayLineDate !== null`. The existing horizontal threshold line stays
   * untouched. See `bucket-format.ts`.
   */
  axis?: IBucketAxisProps;
}

export function EpicLinkageTrendChart({ data, height = DEFAULT_HEIGHT, axis }: Readonly<EpicLinkageTrendChartProps>) {
  const definition = useMetricDefinition('epicLinkage');
  const threshold = successLowerBound(definition);

  return (
    <div className="w-full" aria-label="Epic Linkage trend chart">
      <ResponsiveContainer width="100%" height={height}>
        {/* right: 36 leaves room for the dotted threshold-line label (e.g.
            "90%") at `position: 'right'` — 8px clipped the label
            (PRODOPSAI-384). Matches the YAxis `width=36` for symmetry. */}
        <LineChart data={[...data]} margin={{ top: 6, right: 36, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="period"
            tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            minTickGap={20}
            tickFormatter={axis?.tickFormatter}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={formatPercentTick}
            tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            width={36}
          />
          <Tooltip formatter={formatLinkageTooltip} labelFormatter={axis?.tooltipLabelFormatter} />
          {threshold !== null && (
            <ReferenceLine
              y={threshold}
              stroke="var(--color-muted-foreground)"
              strokeDasharray="4 3"
              strokeOpacity={0.7}
              ifOverflow="extendDomain"
              label={{
                value: `${threshold}%`,
                position: 'right',
                fill: 'var(--color-muted-foreground)',
                fontSize: 10,
              }}
            />
          )}
          {axis?.todayLineDate && (
            <ReferenceLine
              x={axis.todayLineDate}
              stroke="var(--color-muted-foreground)"
              strokeDasharray="4 3"
              strokeOpacity={0.7}
              ifOverflow="extendDomain"
              label={{
                value: 'Today',
                position: 'top',
                fill: 'var(--color-muted-foreground)',
                fontSize: 10,
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="linkagePercent"
            stroke="var(--color-foreground)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
